import { useState } from 'react';
import { HourglassOutlined, MoreOutlined, WarningOutlined } from '@ant-design/icons';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import PopoverButtonsGroup from 'components/new-ui/composed/popover-buttons-group/PopoverButtonsGroup';
import { Declaration } from 'store/declarations/declaration';
import {
    ArchiveButton,
    BarcodeButton,
    CoreTemplateButton,
    DeleteButton,
    DuplicateButton,
    UnarchiveButton,
} from './dashboard-buttons';
import moment from 'moment';

type ActionEvents = {
    onDuplicate?: (record: any) => void;
    onArchive?: (recordIds: string[]) => void;
    onPrint?: (recordId: string) => void;
    onDelete?: (record: any) => void;
    onUnarchive?: (record: any) => void;
    onBarcode?: (record: any) => void;
    onCoreTemplateToggle?: (record: any) => void;
};

type Props = {
    declaration: Declaration;
    actions: ActionEvents;
    canDelete: boolean;
    canArchive: boolean;
};

const GvmsActionsColumn = ({ declaration, actions, canDelete, canArchive }: Props) => {
    const [open, setOpen] = useState(false);

    // Closes the popover when an action is triggered
    const withClose =
        <T extends any[]>(fn?: (...args: T) => void) =>
        (...args: T) => {
            const e = args[0] as any;
            if (e && typeof e.stopPropagation === 'function') {
                e.stopPropagation();
            }

            fn?.(...args);
            setOpen(false);
        };

    if (!declaration.id) {
        return <EmptyCell dataTestId="declaration-actions-row" />;
    }

    const gmrExpiryDays =
        moment(declaration?.gvmsDeclaration?.gmrExpiredAt).startOf('day').diff(moment().startOf('day'), 'days') + 1;
    const gmrAboutToExpire = gmrExpiryDays <= 7 && gmrExpiryDays > 0;

    return (
        <>
            <PopoverButtonsGroup
                open={open}
                onOpenChange={(next) => setOpen(next)}
                buttonEl={
                    <IconButton
                        icon={<MoreOutlined />}
                        tooltipText="Actions"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(true);
                        }}
                    />
                }
            >
                {actions.onBarcode && (
                    <BarcodeButton
                        record={declaration}
                        onClick={actions.onBarcode}
                        disabled={
                            !declaration?.gvmsDeclaration?.gmrId ||
                            !declaration?.gvmsDeclaration?.gmrIdBarcode ||
                            !declaration?.gvmsDeclaration?.gmrExpiredAt
                        }
                    />
                )}
                {actions.onDelete && (
                    <DeleteButton disabled={!canDelete} record={declaration} onClick={withClose(actions.onDelete)} />
                )}
                {actions.onDuplicate && (
                    <DuplicateButton record={declaration} onClick={withClose(actions.onDuplicate)} />
                )}
                {actions.onCoreTemplateToggle && (
                    <CoreTemplateButton record={declaration} onClick={withClose(actions.onCoreTemplateToggle)} />
                )}
                {actions.onArchive && (
                    <ArchiveButton disabled={!canArchive} record={declaration} onClick={withClose(actions.onArchive)} />
                )}
                {actions.onUnarchive && (
                    <UnarchiveButton
                        disabled={!canArchive}
                        record={declaration}
                        onClick={withClose(actions.onUnarchive)}
                    />
                )}
                {gmrAboutToExpire && <HourglassOutlined style={{ color: '#ffd000', fontSize: 20 }} />}
                {(declaration?.actionRequired || declaration?.inspectionRequired) && (
                    <WarningOutlined style={{ color: '#ffb11f', fontSize: 20 }} />
                )}
            </PopoverButtonsGroup>
        </>
    );
};

export default GvmsActionsColumn;
